import { useParams } from 'react-router-dom';
import { useUserGet } from '../hooks/use-user.hook';
import { memo, useCallback, useMemo } from 'react';
import UserDetailsComponent from '../components/user-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { User } from '../types/user.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@utils/date.util';
import { Avatar, Tag } from 'antd';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import QRCodeCard from '@components/common/QRCodeCard';
import PageTitle from '@components/common/PageTitle';

const UserDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useUserGet(id || '');

  const prepareDetailsData = useCallback(
    (dataObj: User | undefined): DetailItem[] => {
      if (!dataObj) return [];

      const detailItems: DetailItem[] = [];
      // prepare data to display in the details card
      Object.entries(dataObj).forEach(([key, value]) => {
        // ignored keys
        if (key === 'tenantId' || key === 'isSysAdmin') return;

        if (key === 'createdAt' || key === 'updatedAt') {
          detailItems.push({
            label: t(`common.${key}.label`),
            value: formatDate(value),
          });
          return;
        }
        if (key === 'id') {
          detailItems.push({
            label: t(`common.${key}.label`),
            value: value.toString(),
          });
          return;
        }
        if (key === 'role') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: value.name,
          });
          return;
        }
        if (key === 'lastLoginAt') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: formatDate(value),
          });
          return;
        }
        if (key === 'emailVerified') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: value ? (
              <Tag color="green">{t('common.yes')}</Tag>
            ) : (
              <Tag color="red">{t('common.no')}</Tag>
            ),
          });
          return;
        }

        if (key === 'picture') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: (
              <Avatar src={value} alt={t('users.picture.label')}>
                {!value && dataObj.name[0]}
              </Avatar>
            ),
          });
          return;
        }

        detailItems.push({
          label: t(`users.${key}.label`),
          value: String(value),
        });
      });

      return detailItems;
    },
    [t]
  );

  if (!id)
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('users.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('users.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  const detailItems = prepareDetailsData(data?.data);

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('users.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('users.title') })}
        showBackButton={true}
        trailTarget={id}
      />
      <div className="row mt-3">
        <div className="col-md-2 d-flex justify-content-center align-items-center">
          <QRCodeCard value={data?.data?.id ?? ''} />
        </div>
        <div className="col-md-10">
          <UserDetailsComponent
            data={detailItems}
            success={data?.success ?? false}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </>
  );
};

export default WithPermissionCheck(memo(UserDetailsContainer), 'user.view');
